import { currentIndex, labelHistoryEntry, useHistoryTrail } from 'hooks/useHistoryTrail';
import { useEffect } from 'react';
import { Location, matchRoutes, useLocation } from 'react-router-dom';
import MainRoutes from 'routes/MainRoutes';
import { pageKey } from 'routes/pageKey';
import { useSelector } from 'store';
import { BreadcrumbState } from 'store/reducers/breadcrumb';
import { getBreadcrumbLeafKey } from 'utils/breadcrumbLeaf';
import { t } from 'utils/getTranslations';

// Mounted once at the App level so every navigation — on any route, before and after login — is
// recorded for the in-app back button (see `useGoBack`).
export function HistoryTrail(): null {
    useHistoryTrail(pageKey);
    return null;
}

type BreadcrumbRoute = { breadcrumbs?: { title: string }[] };

/**
 * The crumb that ends the trail on this page: a record's leaf crumb once the record has published it, or the last
 * section crumb of a page that has no record to name (a list). `undefined` while a record's crumb is still loading.
 */
function pageCrumb(location: Location, { leafKey, leafLabel, leafCrumb }: BreadcrumbState): string | undefined {
    const match = matchRoutes([MainRoutes], location)?.at(-1);
    if (!match) {
        return undefined;
    }
    const leafSlot = getBreadcrumbLeafKey(location.pathname, match.params);
    if (leafSlot) {
        return leafKey === leafSlot ? (leafCrumb ?? leafLabel ?? undefined) : undefined;
    }
    const section = (match.route as BreadcrumbRoute).breadcrumbs?.at(-1);
    return section ? t(section.title) : undefined;
}

/**
 * Names each history entry by its page's crumb, so the new design's back control can say where it leads. Mounted
 * right after {@link HistoryTrail}: effects run in tree order, so the entry is recorded before it is named.
 */
export function HistoryTrailLabels(): null {
    const location = useLocation();
    const breadcrumb = useSelector((state) => state.breadcrumb);

    useEffect(() => {
        const label = pageCrumb(location, breadcrumb);
        if (label) {
            labelHistoryEntry(currentIndex(), `${location.pathname}${location.search}`, label);
        }
    }, [location, breadcrumb]);

    return null;
}
